import React from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { BOOKING_REQUEST_STATUS } from "../../Utils";
import { ArrowForward } from "@material-ui/icons";
import moment from "moment";

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(50%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const CardContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 1.5rem;
  background: var(--card-bg);
  backdrop-filter: blur(var(--glass-blur));
  border-radius: 16px;
  margin: 1rem 0;
  width: 100%;
  animation: ${fadeIn} 0.5s ease-in-out;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-color: #054752;
  }

  @media (max-width: 546px) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
    padding: 1rem;
  }
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-right: 16px;
  object-fit: cover;
`;

const InfoContainer = styled.div`
  flex: 1;

  .content {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
`;

const Name = styled.div`
  font-weight: bold;
  font-size: 1.2rem;
  margin-bottom: 8px;
`;

const FromTo = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Date = styled.div`
  color: #777;
`;

const Status = styled.div`
  color: ${(props) =>
    props.status === "Accepted"
      ? "green"
      : props.status === "Requested"
      ? "grey"
      : "red"};
  font-weight: bold;
`;

const ActionButton = styled.button`
  background-color: ${(props) =>
    props.btnType === "confirm" ? "#054752" : "transparent"};
  color: ${(props) =>
    props.btnType === "confirm" ? "#fff" : "#ef4444"};
  border: ${(props) =>
    props.btnType === "confirm" ? "none" : "1px solid #ef4444"};
  padding: 0.5rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    background-color: ${(props) =>
    props.btnType === "confirm" ? "#006064" : "#ef4444"};
    color: white;
  }
`;

const ActionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  color: orange;
  .expires-at {
    font-size: 14px;
  }

  @media (max-width: 546px) {
    flex-direction: column;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;

  .reject {
    background: transparent;
    border: 1px solid;
    color: #136170;
  }

  @media (max-width: 546px) {
    flex-direction: column;
  }
`;

const HorizontalCard = ({
  avatarSrc,
  name,
  from,
  to,
  date,
  status,
  rideId,
  onActionClick,
  statusType,
  requestId,
  seatsRequired,
  frontSeats,
  backSeats,
  seatType,
  expiresAt,
}) => {
  const isPending = statusType === BOOKING_REQUEST_STATUS.PENDING;
  const isActionable = ![
    BOOKING_REQUEST_STATUS.REJECTED,
    BOOKING_REQUEST_STATUS.CANCELLED,
    BOOKING_REQUEST_STATUS.ACCEPTED,
    BOOKING_REQUEST_STATUS.EXPIRED,
    BOOKING_REQUEST_STATUS.RIDE_CANCELLED,
  ].includes(statusType);

  const renderSeatText = () => {
    const f = parseInt(frontSeats || 0);
    const b = parseInt(backSeats || 0);
    const total = seatsRequired || f + b || 1;

    if (f > 0 && b > 0) {
      return `${total} Seats (${f} Front, ${b} Back)`;
    }
    if (f > 0) {
      return `${total} Front Seat${total > 1 ? "s" : ""}`;
    }
    if (b > 0) {
      return `${total} Back Seat${total > 1 ? "s" : ""}`;
    }
    if (seatType === "front") {
      return `${total} Front Seat${total > 1 ? "s" : ""}`;
    }
    if (seatType === "back") {
      return `${total} Back Seat${total > 1 ? "s" : ""}`;
    }
    return `${total} Seats`;
  };

  return (
    <CardContainer>
      <Avatar src={avatarSrc} alt="User Avatar" />
      <InfoContainer>
        <Name>{name}</Name>
        <Link
          to={`/active-ride/${rideId}`}
          style={{ textDecoration: "none", color: "#1c1c1c" }}
        >
          <FromTo>
            {from} <ArrowForward fontSize="small" /> {to}
          </FromTo>
        </Link>
        <div className="content">
          <p>
            Seats required:{" "}
            <span
              style={{ fontSize: "1.05rem", fontWeight: "bold", color: "teal" }}
            >
              {renderSeatText()}
            </span>
          </p>
          <Date>{date}</Date>
          {status !== "Requested" && <Status status={status}>{status}</Status>}
        </div>
      </InfoContainer>
      <ActionWrapper>
        {isActionable && (
          <StyledButtonContainer>
            <ActionButton
              btnType="confirm"
              onClick={() => onActionClick(requestId, "accept")}
            >
              Confirm
            </ActionButton>
            {isPending && (
              <ActionButton
                btnType="reject"
                onClick={() => onActionClick(requestId, "reject")}
              >
                Decline
              </ActionButton>
            )}
          </StyledButtonContainer>
        )}
        {isPending && expiresAt && (
          <p className="expires-at">
            Expires at {moment(expiresAt).format("h:mm A MMM Do")}
          </p>
        )}
      </ActionWrapper>
    </CardContainer>
  );
};

export default HorizontalCard;
